import { useState, useEffect, useRef } from 'react'
import { useLocation, useMatch, useNavigate, Navigate } from 'react-router-dom'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import BankingService from 'services/Banking'
import useRefreshOnReturn from 'hooks/useRefreshOnReturn'
import BusinessNavbar from 'components/Navbars/BusinessNavbar'
import ItemTabs from 'components/ItemTabs'
import Balance from './Balance'
import Payouts from './Payouts'
import OPERATIONS, { BANKING_PATH, operationPath, operationFromPath } from './Operations'
import AccountData from './AccountData'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { FaUniversity } from 'react-icons/fa'

import {
  PageContainer,
  TabStickyContainer,
  PageTitle,
  Container,
  Content,
  Card,
  CardTitle,
  Caption,
  EmptyState,
  InfoText
} from './styles'

/* As abas da área, na ordem em que aparecem. Cada uma tem a permissão de mesmo nome. */
const TAB_CODES = ['balance', 'payouts', 'account_data']

export default function Banking () {
  const location = useLocation ()
  const bankingTab = location.state?.bankingTab ?? null

  /*
    Sem nenhuma permissão de banking a área inteira some, e a conta nem chega a ser
    buscada — não haveria onde mostrá-la.
  */
  const permittedTabs = TAB_CODES.filter (code => AuthService.hasPermission (`banking.${code}`))

  const [loading, setLoading] = useState (permittedTabs.length > 0)
  const [account, setAccount] = useState (null)
  const [readiness, setReadiness] = useState (null)
  const [error, setError] = useState (null)
  /*
    A aba guardada é conferida contra as que existem: quem usou a área quando havia uma aba
    de transferências tem `transfers` no `localStorage`, e ela abriria a barra sem nenhuma
    aba marcada.
  */
  const [selectedTab, setSelectedTab] = useState (
    [bankingTab, localStorage.getItem ('banking_tab')]
      .find (tab => TAB_CODES.includes (tab)) ?? 'balance'
  )

  /*
    Operação escolhida pelos atalhos do cartão de saldo. Enquanto houver uma, é ela que
    ocupa a área inteira, no lugar das abas — é o que os apps ganham de graça empilhando
    telas. Ela mora na URL, e não no estado, para que o voltar do navegador percorra as telas
    da área em vez de sair dela.
  */
  const navigate = useNavigate ()
  const operationSlug = useMatch (`${BANKING_PATH}/:operation`)?.params.operation ?? null
  const operation = operationFromPath (operationSlug)

  /*
    `banking.transfers` deixou de ser uma aba e continua sendo uma permissão: agora ela
    decide se os atalhos do cartão de saldo aparecem, que é por onde se chega a cada
    operação. Quem não a tem vê o saldo e o extrato, e mais nada.
  */
  const canTransfer = AuthService.hasPermission ('banking.transfers')

  const requestConfig = AuthService.getRequestAuth ()
  const fetching = useRef (false)

  useEffect (() => {
    if (permittedTabs.length > 0) getData ()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
    A situação da conta muda fora daqui — na página do Asaas, que abre em outra aba — e é
    dela que sai o selo do cartão de saldo. Lida uma vez só, ele continuaria dizendo "em
    análise" para quem acabou de ser aprovado.
  */
  useRefreshOnReturn (() => {
    if (permittedTabs.length > 0) getData ()
  })

  /*
    A conta é lida aqui, uma vez a cada volta à aba, e desce por prop — são as abas que
    dependem dela, e é ela que decide quais abas existem.

    Sem conta, a API responde 404 junto com a checagem de prontidão do cadastro, por isso
    o 404 não é tratado como erro e sim como o primeiro passo do fluxo.

    A releitura não acende o `loading`, que já começa ligado: religá-lo trocaria a tela
    inteira pelo indicador a cada volta, inclusive no meio de uma operação. E uma consulta
    por vez, porque os dois eventos de volta chegam juntos.
  */
  const getData = async () => {
    if (fetching.current) return

    fetching.current = true

    try {
      const { data: account } = await cutileiApi.get (
        `/banking_account${BankingService.getOwnerQuery ()}`, requestConfig
      )

      setAccount (account)
      setReadiness (null)
      setError (null)
    } catch (error) {
      if (error.response?.status === 404) {
        setAccount (null)
        setReadiness (error.response.data)
        setError (null)
      } else {
        console.log (error.response ?? error)
        setError (error.response?.data?.message ?? 'Não foi possível carregar sua conta')
      }
    } finally {
      fetching.current = false
      setLoading (false)
    }
  }

  const handleTabNavigate = tabValue => {
    if (operation) navigate (BANKING_PATH)
    setSelectedTab (tabValue)
    localStorage.setItem ('banking_tab', tabValue)
  }

  /*
    Entrar numa operação por dentro da área fica marcado na entrada do histórico. É o que
    permite ao botão de voltar da tela fazer o mesmo que o do navegador.
  */
  const openOperation = next => navigate (operationPath (next), { state: { fromBanking: true } })

  /*
    Para quem chegou pelos atalhos, a entrada anterior do histórico é sempre a tela de cima, e
    voltar para ela deixa o histórico como o botão do navegador deixaria. Quem abriu o
    endereço direto não tem tela anterior dentro da área: a entrada atual é trocada pela de
    cima, em vez de o voltar sair do site.
  */
  const leaveOperation = parent => {
    if (location.state?.fromBanking) return navigate (-1)

    navigate (operationPath (parent), { replace: true })
  }

  /*
    Aberta a conta, as abas aparecem — e a que abre é a dos dados, onde estão os documentos
    que ainda faltam enviar. Sem isso valeria a última aba visitada, que pode ser a de
    movimentação, ainda bloqueada até a conta ser aprovada.
  */
  const handleAccountOpened = openedAccount => {
    setAccount (openedAccount)
    handleTabNavigate ('account_data')
  }

  const TAB_LIST = [
    {
      label: 'Saldo',
      value: 'balance',
      component: (
        <Balance
          account={account}
          requestConfig={requestConfig}
          canTransfer={canTransfer}
          onTabNavigate={handleTabNavigate}
          onOperationNavigate={openOperation}
        />
      )
    },
    {
      label: 'Recebíveis',
      value: 'payouts',
      component: <Payouts requestConfig={requestConfig}/>
    },
    {
      label: 'Meus dados',
      value: 'account_data',
      component: (
        <AccountData
          account={account}
          requestConfig={requestConfig}
          onAccountUpdated={setAccount}
        />
      )
    }
  ].filter (tab => permittedTabs.includes (tab.value))

  const renderContent = () => {
    if (TAB_LIST.length === 0) return (
      <InfoText align='center' style={{marginTop: 20}}>
        Você não tem permissão para acessar esta área
      </InfoText>
    )

    if (loading) return <Loading style={{marginTop: 40}}/>

    if (error) return (
      <Container>
        <Content>
          <Card>
            <CardTitle>Não foi possível carregar sua conta</CardTitle>
            <Caption>{error}</Caption>
          </Card>
        </Content>
      </Container>
    )

    /*
      Endereço de operação que não se abre agora — conta ainda sem abrir ou em análise,
      operação que não existe, ou quem não movimenta dinheiro — volta para a área, em vez de
      mostrar outra tela debaixo de uma URL que diz "Sacar".
    */
    const canOpenOperation = operation && account?.canTransact
      && BankingService.canMoveMoney () && canTransfer

    if (operationSlug && !canOpenOperation) return <Navigate to={BANKING_PATH} replace/>

    /*
      Antes de a conta existir não há saldo nem movimentação para mostrar. As abas dão
      lugar ao formulário de abertura, que é o único caminho a partir daqui.
    */
    if (!account) {
      if (!permittedTabs.includes ('account_data')) return (
        <Container>
          <Content>
            <Card>
              <EmptyState>
                <FaUniversity size={26}/>
                <b>A conta Cutilei ainda não foi aberta</b>
                <p>
                  {'Peça ao responsável pelo salão que abra a conta digital para '
                  + 'começarem a usar o Cutilei Pay'}
                </p>
              </EmptyState>
            </Card>
          </Content>
        </Container>
      )

      return (
        <AccountData
          readiness={readiness}
          requestConfig={requestConfig}
          onAccountOpened={handleAccountOpened}
        />
      )
    }

    /*
      Conta aberta ainda não é conta usável. Até o banco aprovar não há saldo para ver nem
      movimentação para fazer, e as duas abas só saberiam repetir que a conta está em
      análise — a área inteira vira o acompanhamento, que é onde está o que falta fazer.
    */
    if (!account.canTransact) {
      if (!permittedTabs.includes ('account_data')) return (
        <Container>
          <Content>
            <Card>
              <EmptyState>
                <FaUniversity size={26}/>
                <b>A conta Cutilei está em análise</b>
                <p>
                  {'O banco ainda está conferindo os dados do salão. O Cutilei Pay libera '
                  + 'assim que a conta for aprovada'}
                </p>
              </EmptyState>
            </Card>
          </Content>
        </Container>
      )

      return (
        <AccountData
          account={account}
          requestConfig={requestConfig}
          onAccountUpdated={setAccount}
        />
      )
    }

    /*
      Com uma operação escolhida, ela ocupa a área inteira e as abas somem — como uma tela
      empilhada nos apps. O `parent` leva o voltar da área Pix de volta à lista de formas de
      pagar, em vez de saltar direto para o saldo.
    */
    const operationEntry = operation ? OPERATIONS[operation] : null

    if (operationEntry) {
      const { Component, parent } = operationEntry

      return (
        <Component
          account={account}
          requestConfig={requestConfig}
          onBack={() => leaveOperation (parent)}
          onOperationNavigate={openOperation}
          onTabNavigate={handleTabNavigate}
          onCompleted={getData}
        />
      )
    }

    return (
      <>
        <TabStickyContainer>
          <ItemTabs
            tabs={TAB_LIST}
            tabWidth={150}
            selectedTab={selectedTab}
            onTabChange={handleTabNavigate}
            containerStyle={{paddingBottom: 7}}
          />
        </TabStickyContainer>
        {(TAB_LIST.find (tab => tab.value === selectedTab) ?? TAB_LIST[0]).component}
      </>
    )
  }

  return (
    <PageContainer>
      <BusinessNavbar/>
      <PageTitle>Cutilei Pay</PageTitle>
      <Caption align='center' style={{marginBottom: 6}}>
        Sua conta digital Cutilei
      </Caption>
      {renderContent ()}
    </PageContainer>
  )
}
